import React from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../hooks/useAppContext';
import { Role, ReportType, ProjectWorkflowStatus, ReportType as RT, Report, ProjectDetails } from '../../types';
import { Briefcase, BarChart2, User as UserIcon, FileText, Wrench, Download, Users2, Package, Banknote, ArrowRight } from 'lucide-react';
import useAppStore from '../../store/useAppStore';

const ModernServiceCard: React.FC<{ to: string; title: string; icon: React.ElementType; color: string; delay: number }>
    = ({ to, title, icon: Icon, color, delay }) => (
        <Link
            to={to}
            className="block group relative"
            aria-label={title}
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="glass-card rounded-2xl p-6 h-full relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-300">
                {/* Background Gradient Blob */}
                <div className={`absolute -right-10 -top-10 w-32 h-32 bg-${color}-500/20 rounded-full blur-3xl group-hover:bg-${color}-500/30 transition-colors`}></div>

                <div className="relative z-10 flex flex-col items-start gap-4">
                    <div className={`p-3 rounded-xl bg-${color}-500/10 text-${color}-400 group-hover:text-white group-hover:bg-${color}-500 transition-colors duration-300`}>
                        <Icon size={28} />
                    </div>

                    <div className="flex items-center justify-between w-full">
                        <span className="font-bold text-lg text-slate-100">{title}</span>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-slate-800/50 text-slate-400 group-hover:bg-${color}-500 group-hover:text-white transition-all opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0`}>
                            <ArrowRight size={16} className="rotate-180" />
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );

const ModernEmployeeDashboardScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports, currentUserLedTeam } = useAppStore();
    if (!user) return null;

    const hasLedTeam = !!currentUserLedTeam;

    type CardDef = { key: string; to: string; title: string; icon: React.ElementType; show: boolean; color: string };
    const cards: CardDef[] = [
        { key: 'team-projects', to: '/team-projects', title: t('teamProjects'), icon: Users2, show: true, color: 'orange' },
        { key: 'sales', to: '/sales', title: t('salesReports'), icon: FileText, show: true, color: 'blue' },
        { key: 'maintenance', to: '/maintenance', title: t('maintenanceReports'), icon: Wrench, show: true, color: 'emerald' },
        { key: 'project', to: '/project-dashboard', title: t('projectReports'), icon: Briefcase, show: true, color: 'orange' },
        { key: 'log', to: '/log', title: t('reportsLog'), icon: BarChart2, show: true, color: 'cyan' },
        { key: 'workflow', to: '/workflow', title: t('importExport'), icon: Download, show: true, color: 'indigo' },
        { key: 'packages', to: '/packages', title: 'إدارة البكجات', icon: Package, show: true, color: 'yellow' },
        { key: 'profile', to: '/profile', title: t('profile'), icon: UserIcon, show: true, color: 'rose' }
    ];

    // Compute Team Projects KPIs
    const teamProjects = currentUserLedTeam
        ? reports.filter(r => r.type === ReportType.Project && r.assignedTeamId === currentUserLedTeam.id)
        : [];

    const total = teamProjects.length;
    const pendingAcceptance = teamProjects.filter(p => p.projectWorkflowStatus === ProjectWorkflowStatus.PendingTeamAcceptance).length;

    return (
        <div className="space-y-8 animate-content-fade-in">
            {/* Welcome Section */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 to-purple-600 p-8 md:p-10 shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                <div className="relative z-10">
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                        {t('goodMorning')}, {user.name.split(' ')[0]} 👋
                    </h1>
                    <p className="text-blue-100 text-lg opacity-90">
                        {t('quickOverview')}
                    </p>
                </div>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {cards.filter(c => c.show).map((card, index) => (
                    <ModernServiceCard
                        key={card.key}
                        to={card.to}
                        title={card.title}
                        icon={card.icon}
                        color={card.color}
                        delay={index * 100}
                    />
                ))}
            </div>

            {/* Team Lead Stats */}
            {hasLedTeam && (
                <div className="space-y-4">
                    <h3 className="text-xl font-bold text-slate-200 px-2 border-r-4 border-orange-500">{t('teamProjects')}</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <Link to="/project-dashboard" className="block group">
                            <div className="glass-panel p-6 rounded-2xl hover:bg-slate-800/80 transition-colors">
                                <div className="text-sm text-slate-400 mb-1">إجمالي المشاريع</div>
                                <div className="text-3xl font-bold text-white group-hover:text-orange-400 transition-colors">{total}</div>
                            </div>
                        </Link>
                        <Link to="/team-projects?status=pending-acceptance" className="block group">
                            <div className="glass-panel p-6 rounded-2xl hover:bg-slate-800/80 transition-colors">
                                <div className="text-sm text-slate-400 mb-1">{t('projectStatusPendingTeamAcceptance')}</div>
                                <div className="text-3xl font-bold text-white group-hover:text-orange-400 transition-colors">{pendingAcceptance}</div>
                            </div>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernEmployeeDashboardScreen;
